/**
 * Love Calendar - Modo tarjeta única (día actual)
 */

import { getTokenFromURL, validateToken, saveToken, getSavedToken } from './auth.js';
import { showCardByDate, hasDataForDate } from './card.js';
import { getTodayDate, addDays, compareDates } from './date-utils.js';
import { applyChristmasTheme } from './calendar.js';

let currentDate = null;

async function init() {
  registerServiceWorker();
  setupErrorBypass();

  let token = getTokenFromURL() || getSavedToken();
  const isValid = await validateToken(token);
  if (!isValid) {
    showScreen('error');
    return;
  }

  saveToken(token);
  await showTodayCard();
  updateSeasonalTheme();
}

function setupErrorBypass() {
  const btn = document.getElementById('btn-bypass-error');
  if (btn) {
    btn.onclick = async () => {
      console.log('Bypassing token validation... Life is risky!');
      saveToken('bypass-token-bugs-life'); 
      await showTodayCard();
      updateSeasonalTheme();
    };
  }
}

function updateSeasonalTheme() {
  const today = getTodayDate();
  const month = today.getMonth(); // 0-11
  const day = today.getDate();
  const body = document.body;
  const snowContainer = document.getElementById('snow-container');

  // Reset classes
  body.classList.remove('theme-christmas', 'theme-winter', 'theme-default');

  // Christmas Season: Nov 30 - Jan 6
  const isChristmas = (month === 10 && day >= 30) || (month === 11) || (month === 0 && day <= 6);
  
  if (isChristmas) {
    body.classList.add('theme-christmas');
    if (snowContainer) snowContainer.style.display = 'block';
    createSnowflakes();
  } else if (month === 0 || month === 1) { // Jan - Feb (Winter)
    body.classList.add('theme-winter');
    if (snowContainer) snowContainer.style.display = 'none'; // Or maybe light snow?
  } else {
    body.classList.add('theme-default');
    if (snowContainer) snowContainer.style.display = 'none';
  }
}

function createSnowflakes() {
  const container = document.getElementById('snow-container');
  if (!container || container.children.length > 0) return;

  for (let i = 0; i < 50; i++) {
    const snowflake = document.createElement('div');
    snowflake.classList.add('snowflake');
    snowflake.style.left = Math.random() * 100 + 'vw';
    snowflake.style.animationDuration = Math.random() * 3 + 2 + 's';
    snowflake.style.opacity = Math.random();
    snowflake.innerText = '❄';
    container.appendChild(snowflake);
  }
}

function showScreen(screenName) {
  document.querySelectorAll('.screen').forEach(s => s.classList.remove('active'));
  const screen = document.getElementById(`${screenName}-screen`);
  if (screen) screen.classList.add('active');
}

async function showTodayCard() {
  currentDate = getTodayDate();
  showScreen('card');
  await showCardByDate(currentDate);
  setupNav();
  setupSwipe();
  await updateNavButtons();
}

function setupSwipe() {
  let touchStartX = 0;
  let touchStartY = 0;
  const container = document.querySelector('.card-container');

  if (!container) return;

  container.addEventListener('touchstart', (e) => {
    touchStartX = e.changedTouches[0].screenX;
    touchStartY = e.changedTouches[0].screenY;
  }, { passive: true });

  container.addEventListener('touchend', (e) => {
    const touchEndX = e.changedTouches[0].screenX;
    const touchEndY = e.changedTouches[0].screenY;
    
    handleSwipe(touchStartX, touchStartY, touchEndX, touchEndY);
  }, { passive: true });
}

function handleSwipe(startX, startY, endX, endY) {
  const diffX = endX - startX;
  const diffY = endY - startY;
  const threshold = 50; // Mínimo desplazamiento para considerar swipe

  // Si el movimiento horizontal es mayor que el vertical (para no interferir con scroll)
  if (Math.abs(diffX) > Math.abs(diffY)) {
    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        // Swipe Derecha -> Anterior
        const prevBtn = document.getElementById('btn-prev');
        if (prevBtn && !prevBtn.disabled) navigate(-1);
      } else {
        // Swipe Izquierda -> Siguiente
        const nextBtn = document.getElementById('btn-next');
        if (nextBtn && !nextBtn.disabled) navigate(1);
      }
    }
  }
}

function setupNav() {
  const prevBtn = document.getElementById('btn-prev');
  const nextBtn = document.getElementById('btn-next');

  if (prevBtn) {
    prevBtn.onclick = () => navigate(-1);
  }

  if (nextBtn) {
    nextBtn.onclick = () => navigate(1);
  }
}

async function navigate(direction) {
  const newDate = addDays(currentDate, direction);
  
  // Check future limit
  if (direction > 0 && compareDates(newDate, getTodayDate()) > 0) {
    return;
  }

  // Check if data exists
  const exists = await hasDataForDate(newDate);
  if (!exists) return;

  const scene = document.querySelector('.scene');
  
  // Determine classes
  // Si voy al siguiente (derecha), el actual sale a la izquierda (slide-left-out) y el nuevo entra desde la derecha (slide-left-in)
  // Si voy al anterior (izquierda), el actual sale a la derecha (slide-right-out) y el nuevo entra desde la izquierda (slide-right-in)
  
  const outClass = direction > 0 ? 'slide-left-out' : 'slide-right-out';
  const inClass = direction > 0 ? 'slide-left-in' : 'slide-right-in';

  // Animate Out
  scene.classList.add(outClass);

  // Wait for animation
  await new Promise(r => setTimeout(r, 500));

  // Update State
  currentDate = newDate;
  await showCardByDate(currentDate);
  await updateNavButtons();

  // Reset classes and Animate In
  scene.classList.remove(outClass);
  scene.classList.add(inClass);

  // Clean up
  setTimeout(() => {
    scene.classList.remove(inClass);
  }, 500);
}

async function updateNavButtons() {
  const prevBtn = document.getElementById('btn-prev');
  const nextBtn = document.getElementById('btn-next');

  if (prevBtn) {
    const prevDate = addDays(currentDate, -1);
    const prevExists = await hasDataForDate(prevDate);
    prevBtn.disabled = !prevExists;
    prevBtn.style.opacity = prevExists ? '1' : '0.3';
    prevBtn.style.cursor = prevExists ? 'pointer' : 'not-allowed';
  }

  if (nextBtn) {
    const nextDate = addDays(currentDate, 1);
    const isFuture = compareDates(nextDate, getTodayDate()) > 0;
    const nextExists = await hasDataForDate(nextDate);
    
    const canGoNext = !isFuture && nextExists;
    
    nextBtn.disabled = !canGoNext;
    nextBtn.style.opacity = canGoNext ? '1' : '0.3';
    nextBtn.style.cursor = canGoNext ? 'pointer' : 'not-allowed';
  }
}

async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      await navigator.serviceWorker.register('/service-worker.js');
    } catch (error) {
      console.error('SW error:', error);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
